/**
 * Royal Road friends (Phase 2c): who they are, the game each brings and what
 * they say. Plain data, no Pixi, so the app, the engine and tests share it.
 */
import type { FriendGame, FriendId, RoadLamp, RoadState } from './road';
import { RUN_NAME } from './runName';

/** A friend's two poses: saying hello, and cheering a win. */
export type FriendPose = 'greet' | 'cheer';

export interface FriendInfo {
  id: FriendId;
  /** As the friend is introduced ("Sir Clank"). */
  name: string;
  /** Short enough for a lamp's label ("Clank"). */
  short: string;
  game: FriendGame;
  /** Their game's name ("Hoot's Parcels"). */
  title: string;
  /** What they say when they join. */
  line: string;
  /** The game their lamp offers instead, with its name and their words,
   *  to a player with Gamble switched off, when theirs is a gamble. The
   *  server decides: a lamp naming this game (`RoadLamp.game`) offers it. */
  noGamble?: { game: FriendGame; title: string; line: string };
}

/** Every road friend, by id. The server says which friend lights which
 *  lamp (`RoadLamp.friend`); their words live here. */
export const FRIENDS: Record<FriendId, FriendInfo> = {
  hoot: {
    id: 'hoot', name: 'Hoot', short: 'Hoot', game: 'pick', title: 'Hoot’s Parcels',
    line: 'Twit-twoo! You fixed my tree. I’ve wrapped three parcels: pick one, any one!',
  },
  willow: {
    id: 'willow', name: 'Willow', short: 'Willow', game: 'hilo', title: 'Acorn Hi-Lo',
    line: 'Easy now... I’m Willow. Fancy a game of higher or lower? Ties go your way.',
  },
  bramble: {
    id: 'bramble', name: 'Bramble', short: 'Bramble', game: 'smash', title: 'Mushroom Smash',
    line: 'Oi! Mind the mushrooms! Oh, go on then. Smash a few and see what’s inside.',
  },
  luna: {
    id: 'luna', name: 'Luna', short: 'Luna', game: 'match', title: 'Moonflower Match',
    line: `The glade is glowing again. Find the pairs! In a ${RUN_NAME}, my gold pair can win the Gold Pot.`,
  },
  clank: {
    id: 'clank', name: 'Sir Clank', short: 'Clank', game: 'dig', title: 'Dungeon Dig',
    line: 'Halt! Oh, it’s you. There’s treasure under the dungeon floor. Mind the bombs!',
    // Dig is bank or bust, so with Gamble off his lamp offers parcels.
    noGamble: {
      game: 'pick', title: 'Armoury Parcels',
      line: 'Halt! Oh, it’s you. I’ve three parcels from the armoury. Pick one and it’s yours!',
    },
  },
  ruby: {
    id: 'ruby', name: 'Lady Ruby', short: 'Ruby', game: 'scratch', title: 'Jewel Scratch',
    line: 'Darling! Scratch my card and match three jewels.',
  },
  seal: {
    id: 'seal', name: 'Old Seal', short: 'Seal', game: 'cups', title: 'Find the Seal',
    line: 'Watch the helmets closely, traveller. Where did the seal go?',
  },
  crown: {
    id: 'crown', name: 'The Crown', short: 'Crown', game: 'wheel', title: 'Crown Wheel',
    line: `You found me! Spin my wheel. In a ${RUN_NAME}, it can stop on the Gold Pot.`,
  },
  // Stormy Seas
  stella: {
    id: 'stella', name: 'Stella', short: 'Stella', game: 'cups', title: 'Shell Shuffle',
    line: 'Hiya, I’m Stella! You cleared my rock pool. Five arms, three cups: follow the coins round and round!',
  },
  nipper: {
    id: 'nipper', name: 'Captain Nipper', short: 'Nipper', game: 'dig', title: 'Sandcastle Dig',
    line: 'Click-clack! You rebuilt my castle! I’ve buried treasure all round the moat. Dig it up, but mind the bombs!',
    // Dig is bank or bust, so with Gamble off his lamp offers parcels.
    noGamble: {
      game: 'pick', title: 'Seaweed Parcels',
      line: 'Click-clack! You rebuilt my castle! I’ve wrapped three parcels in seaweed. Grab whichever you fancy!',
    },
  },
  skipper: {
    id: 'skipper', name: 'Skipper', short: 'Skipper', game: 'hilo', title: 'Wave Hi-Lo',
    line: 'Wheee! I’m Skipper. Up I leap, down I dive! Will my next card leap higher or dive lower? Ties go your way.',
  },
  pearl: {
    id: 'pearl', name: 'Pearl', short: 'Pearl', game: 'match', title: 'Grotto Pairs',
    line: `The grotto is shining again! I’m Pearl. Turn my shells and match them two by two. In a ${RUN_NAME}, my gold pair can win the Gold Pot.`,
  },
  // Frost Reels
  mitzi: {
    id: 'mitzi', name: 'Mitzi', short: 'Mitzi', game: 'pick', title: 'Woolly Parcels',
    line: 'Brr, that’s better! I’m Mitzi. I’ve knitted three cosy parcels. Choose the one you like best!',
  },
  bjorn: {
    id: 'bjorn', name: 'Bjorn', short: 'Bjorn', game: 'whack', title: 'Snow Mole Whack',
    line: 'Hello! I’m Bjorn. My hut’s back on the ice, but the snow moles keep popping up. Bonk as many as you can!',
  },
  pippin: {
    id: 'pippin', name: 'Pippin', short: 'Pippin', game: 'scratch', title: 'Frosty Scratch',
    line: 'Ah, smooth ice at last! I’m Pippin. Rub the frost off my card: three snowy pictures alike will win.',
  },
  glimmer: {
    id: 'glimmer', name: 'Glimmer', short: 'Glimmer', game: 'match', title: 'Starlight Match',
    line: `The lights are dancing again! I’m Glimmer. Flip my starlit cards to find each twin. In a ${RUN_NAME}, my gold pair can win the Gold Pot.`,
  },
  // Dustwood Reels
  digby: {
    id: 'digby', name: 'Digby', short: 'Digby', game: 'dig', title: 'Gold Rush Dig',
    line: 'Howdy, partner! You fixed up the camp. There’s gold under these squares. Dig some up, but mind the bombs!',
    // Dig is bank or bust, so with Gamble off his lamp offers parcels.
    noGamble: {
      game: 'pick', title: 'Burrow Parcels',
      line: 'Howdy, partner! You fixed up the camp. I’ve stashed three parcels in my burrow. Have a rummage and take one!',
    },
  },
  shelby: {
    id: 'shelby', name: 'Old Shelby', short: 'Shelby', game: 'cups', title: 'Find the Nugget',
    line: 'Well now... no rush. I’m Old Shelby. Watch the cups as slowly as I do. Which one is hiding the nugget?',
  },
  biscuit: {
    id: 'biscuit', name: 'Biscuit', short: 'Biscuit', game: 'scratch', title: 'Horseshoe Scratch',
    line: 'Hee-haw! The store’s open again! Brush the dust off my lucky card and find a matching three.',
  },
  goldie: {
    id: 'goldie', name: 'Goldie', short: 'Goldie', game: 'wheel', title: 'Sundown Wheel',
    line: `Well, I never! Folk say I only come out at sundown. Give my wagon wheel a whirl. In a ${RUN_NAME}, it can stop on the Gold Pot.`,
  },
  // Neon Reels
  presto: {
    id: 'presto', name: 'The Great Presto', short: 'Presto', game: 'cups', title: 'Presto’s Cup Trick',
    line: 'Ta-da! My shop is sparkling again. Keep your eyes on my cups: the coins vanish under one of them!',
  },
  sprocket: {
    id: 'sprocket', name: 'Sprocket', short: 'Sprocket', game: 'pick', title: 'Sprocket’s Parcels',
    line: 'Beep boop! Power restored. I’ve packed three parcels of spare parts. Which one will you open?',
  },
  dazzle: {
    id: 'dazzle', name: 'Miss Dazzle', short: 'Dazzle', game: 'hilo', title: 'Mirror Ball Hi-Lo',
    line: 'Ooh, the mirror ball is spinning again, sweetie! Will my next card glide higher or lower? Ties go your way.',
  },
  nova: {
    id: 'nova', name: 'Nova', short: 'Nova', game: 'wheel', title: 'Spotlight Wheel',
    line: `The marquee is lit and I’m back on stage! Step into my light and spin. In a ${RUN_NAME}, it can stop on the Gold Pot.`,
  },
  // Desert Caravan
  humphrey: {
    id: 'humphrey', name: 'Humphrey', short: 'Humphrey', game: 'pick', title: 'Saddlebag Pick',
    line: 'Ahh, water in the well again. Thank you! I’m Humphrey. Each of my saddlebags hides a gift: choose one.',
  },
  scout: {
    id: 'scout', name: 'Scout', short: 'Scout', game: 'hilo', title: 'Dune Hi-Lo',
    line: 'Psst! Up here! I’m Scout, and I watch the dunes. Is the next card up the dune or down it? Ties go your way.',
  },
  saffron: {
    id: 'saffron', name: 'Saffron', short: 'Saffron', game: 'cups', title: 'Spice Cups',
    line: 'Welcome, welcome to my stall! My spice cups shuffle fast. Follow the coins if you can.',
  },
  sol: {
    id: 'sol', name: 'Sol', short: 'Sol', game: 'wheel', title: 'Sun Wheel',
    line: `The gate is open and I’m shining again! Turn my Sun Wheel. In a ${RUN_NAME}, it can stop on the Gold Pot.`,
  },
  // Sherbet Square
  crumb: {
    id: 'crumb', name: 'Mrs Crumb', short: 'Crumb', game: 'scratch', title: 'Sugar Scratch',
    line: 'Ooh, a customer! The shop’s open again. Scratch away the sugar and see which sweets match.',
  },
  ginger: {
    id: 'ginger', name: 'Chef Ginger', short: 'Ginger', game: 'smash', title: 'Toffee Smash',
    line: 'Mind, it’s hot! My stove’s lit again. Crack three of my toffees: one of them hides a double!',
  },
  bertie: {
    id: 'bertie', name: 'Bertie', short: 'Bertie', game: 'hilo', title: 'Bandstand Hi-Lo',
    line: 'Strike up the band! The bandstand’s back. Will the next note go higher or lower? Ties go your way.',
  },
  sundae: {
    id: 'sundae', name: 'Sundae', short: 'Sundae', game: 'match', title: 'Sherbet Match',
    line: `The fountain’s fizzing again! Swirl my sweet cards and find each pair. In a ${RUN_NAME}, my gold pair can win the Gold Pot.`,
  },
};

/** Each road slot's friends, in the order its road meets them (the art a
 *  slot loads with its road). */
export const THEME_FRIENDS: Record<string, FriendId[]> = {
  forest: ['hoot', 'willow', 'bramble', 'luna'],
  royal: ['clank', 'ruby', 'seal', 'crown'],
  underwater: ['stella', 'nipper', 'skipper', 'pearl'],
  frost: ['mitzi', 'bjorn', 'pippin', 'glimmer'],
  frontier: ['digby', 'shelby', 'biscuit', 'goldie'],
  neon: ['presto', 'sprocket', 'dazzle', 'nova'],
  caravan: ['humphrey', 'scout', 'saffron', 'sol'],
  sherbet_square: ['crumb', 'ginger', 'bertie', 'sundae'],
};

/** The games that can win a slot's gold pot. */
export const GOLD_GAMES: readonly FriendGame[] = ['match', 'wheel'];

/** The texture alias of a friend's pose, or a game's lamp icon, in the
 *  road's art (`road:friend_hoot`, `road:friend_hoot_cheer`, `road:game_pick`). */
export const friendAlias = (id: FriendId, pose: FriendPose = 'greet') =>
  `road:friend_${id}${pose === 'cheer' ? '_cheer' : ''}`;
export const gameAlias = (game: FriendGame) => `road:game_${game}`;

/** The friend behind a lamp, if it is a friend's lamp, with the game the
 *  lamp offers: their own, or (Gamble off) the one standing in for it. */
export function friendOf(lamp: Pick<RoadLamp, 'friend' | 'game'>): FriendInfo | null {
  const friend = lamp.friend ? FRIENDS[lamp.friend] ?? null : null;
  if (!friend?.noGamble || lamp.game !== friend.noGamble.game) return friend;
  return { ...friend, ...friend.noGamble };
}

/** The friend whose lamp on a road offers `game`, under the name it has
 *  there (a Gamble-off stand-in's own name), or null when no friend's lamp
 *  does. Each road's friends bring different games. */
export function gameFriend(road: Pick<RoadState, 'lamps'> | null, game: string): FriendInfo | null {
  const lamp = road?.lamps.find((l) => l.friend && l.game === game);
  return lamp ? friendOf(lamp) : null;
}

/** The friend who brings Hi-Lo to a road: their game's name heads the Hi-Lo
 *  dialog. Willow's when the road has no Hi-Lo friend. */
export function hiloFriend(road: Pick<RoadState, 'lamps'> | null): FriendInfo {
  return gameFriend(road, 'hilo') ?? FRIENDS.willow;
}

/** The friend a road's gold pot waits for, if it has one. */
export function goldFriend(road: Pick<RoadState, 'lamps'> | null): FriendInfo | null {
  const lamp = road?.lamps.find((l) => l.game && GOLD_GAMES.includes(l.game));
  return lamp ? friendOf(lamp) : null;
}

/** Friends whose lamps were dark in `before` and are lit in `after`: the
 *  friends who have just joined, in lamp order. A first load (no `before`)
 *  is nobody joining, and so is another slot's road. */
export function friendsJoined(before: Pick<RoadState, 'lamps'> | null, after: Pick<RoadState, 'lamps'> | null): FriendInfo[] {
  if (!before || !after) return [];
  const wasLit = new Set(before.lamps.filter((l) => l.lit).map((l) => l.id));
  return after.lamps
    .filter((l) => l.lit && l.friend && !wasLit.has(l.id) && before.lamps.some((b) => b.id === l.id && b.friend === l.friend))
    .map((l) => friendOf(l))
    .filter((f): f is FriendInfo => f !== null);
}
